import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { clientsService, Client } from '@/services/clients.service';
import { invoicesService } from '@/services/invoices.service';
import { tasksService } from '@/services/tasks.service';
import { projectsService } from '@/services/projects.service';
import { integrationsService } from '@/services/integrations.service';
import { getClientAnalytics } from '@/services/analytics.service';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/components/ui/Toast';
import type { Invoice, Task, Project, Integration } from '@/types';
import { cn } from '@/lib/utils';
import {
  LayoutGrid,
  FileText,
  ClipboardList,
  FolderOpen,
  Info,
  Plus,
  Pencil,
  Trash2,
  Building2,
  User,
  Clock,
  CheckCircle2,
  Circle,
  Calendar,
  Github,
  Link as LinkIcon,
} from 'lucide-react';

type TabType = 'overview' | 'invoices' | 'tasks' | 'projects' | 'details';

const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'invoices', label: 'Invoices', icon: FileText },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'projects', label: 'Projects', icon: FolderOpen },
  { id: 'details', label: 'Details', icon: Info },
];

const statusConfig = {
  'not-started': { label: 'Not Started', icon: Circle, className: 'text-muted-foreground' },
  'in-progress': { label: 'In Progress', icon: Clock, className: 'text-blue-600' },
  'completed': { label: 'Completed', icon: CheckCircle2, className: 'text-green-600' },
};

const priorityConfig = {
  high: { label: 'High', className: 'border-red-500 text-red-500' },
  medium: { label: 'Medium', className: 'border-muted-foreground text-muted-foreground' },
  low: { label: 'Low', className: 'border-green-600 text-green-600' },
};

const invoiceStatusConfig = {
  draft: { label: 'Draft', className: 'border-muted-foreground text-muted-foreground' },
  sent: { label: 'Sent', className: 'border-blue-500 text-blue-500' },
  paid: { label: 'Paid', className: 'border-green-600 text-green-600' },
  overdue: { label: 'Overdue', className: 'border-red-500 text-red-500' },
  cancelled: { label: 'Cancelled', className: 'border-muted-foreground text-muted-foreground' },
};

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [client, setClient] = useState<Client | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [clientData, invoicesData, tasksData, projectsData, integrationsData] = await Promise.all([
        clientsService.getClient(id),
        invoicesService.getInvoices().then(inv => inv.filter(i => i.clientId === id)),
        tasksService.getTasks().then(t => t.filter(task => task.clientId === id)),
        projectsService.getProjects().then(p => p.filter(proj => proj.clientId === id)),
        integrationsService.getIntegrations().then(ints => ints.filter(i => i.clientId === id)),
      ]);

      setClient(clientData);
      setInvoices(invoicesData);
      setTasks(tasksData);
      setProjects(projectsData);
      setIntegrations(integrationsData);

      // Load analytics if user is authenticated
      if (user) {
        try {
          const idToken = await user.getIdToken();
          const analyticsData = await getClientAnalytics(id, idToken);
          setAnalytics(analyticsData);
        } catch (err) {
          console.error('Error loading analytics:', err);
        }
      }
    } catch (err: any) {
      console.error('Error loading client:', err);
      setError(err.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      setDeleting(true);
      await clientsService.deleteClient(id);
      toast.success('Client deleted successfully');
      navigate('/clients');
    } catch (err: any) {
      console.error('Error deleting client:', err);
      toast.error(err.message || 'Failed to delete client');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-8 w-64" />
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (error || !client) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-foreground mb-2">
            {error ? 'Error loading client' : 'Client not found'}
          </h2>
          {error && <p className="text-destructive mb-4">{error}</p>}
          <Button asChild variant="outline">
            <Link to="/clients">Back to clients</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const totalHours = analytics?.totalHours ?? 0;
  const totalEarnings = analytics?.totalEarnings ?? 0;
  const activeTasks = tasks.filter(t => t.status !== 'completed').length;
  const activeProjects = projects.length;

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Clients', href: '/clients' }, { label: client.name }]} />

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            {client.type === 'business' ? (
              <Building2 className="w-7 h-7 text-primary" />
            ) : (
              <User className="w-7 h-7 text-primary" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{client.name}</h1>
            {client.businessName && (
              <p className="text-muted-foreground">{client.businessName}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button asChild>
            <Link to={`/finances/invoices/new?clientId=${client.id}`}>
              <Plus className="w-4 h-4 mr-2" />
              Invoice
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={`/clients/${client.id}/edit`}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Link>
          </Button>
          <Button variant="outline" onClick={() => setShowDeleteModal(true)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border border-border rounded-lg p-1 mb-6 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          let count: number | undefined;
          if (tab.id === 'invoices') count = invoices.length;
          if (tab.id === 'tasks') count = tasks.length;
          if (tab.id === 'projects') count = projects.length;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {count !== undefined && count > 0 && (
                <span className={cn(
                  'text-xs',
                  activeTab === tab.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                )}>
                  ({count})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Total Hours</p>
              <p className="text-2xl font-bold text-foreground">{totalHours.toFixed(1)}h</p>
            </div>
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Earnings</p>
              <p className="text-2xl font-bold text-foreground">€{Math.round(totalEarnings).toLocaleString()}</p>
            </div>
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Active Tasks</p>
              <p className="text-2xl font-bold text-foreground">{activeTasks}</p>
            </div>
            <div className="border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Projects</p>
              <p className="text-2xl font-bold text-foreground">{activeProjects}</p>
            </div>
          </div>

          {/* Linked Integrations */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Linked Integrations</h3>
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings/integrations">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Manage
                </Link>
              </Button>
            </div>
            {integrations.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No integrations linked to this client.{' '}
                <Link to="/settings/integrations" className="text-primary hover:underline">
                  Link an integration
                </Link>
              </p>
            ) : (
              <div className="space-y-3">
                {integrations.map((integration) => (
                  <div key={integration.id} className="flex items-center gap-3">
                    {integration.avatarUrl ? (
                      <img
                        src={integration.avatarUrl}
                        alt={integration.accountLogin}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-[#24292e] rounded-full flex items-center justify-center text-white">
                        <Github className="w-4 h-4" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{integration.accountLogin}</p>
                      <p className="text-xs text-muted-foreground">
                        {integration.syncRepos.length === 0
                          ? 'All repositories'
                          : `${integration.syncRepos.length} repositories`}
                      </p>
                    </div>
                    <Badge variant="outline">{integration.accountType}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="border border-border rounded-lg overflow-hidden">
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No invoices yet</p>
              <Button asChild>
                <Link to={`/finances/invoices/new?clientId=${client.id}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    onClick={() => navigate(`/finances/invoices/${invoice.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-medium text-primary">
                      {invoice.invoiceNumber}
                    </TableCell>
                    <TableCell className="text-foreground">
                      {new Date(invoice.issueDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium text-foreground">
                      €{invoice.total.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={invoiceStatusConfig[invoice.status].className}>
                        {invoiceStatusConfig[invoice.status].label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="border border-border rounded-lg overflow-hidden">
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No tasks yet</p>
              <Button asChild>
                <Link to={`/tasks/new?clientId=${client.id}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Deadline</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => {
                  const statusConf = statusConfig[task.status];
                  const priorityConf = priorityConfig[task.priority];
                  const StatusIcon = statusConf.icon;

                  return (
                    <TableRow
                      key={task.id}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium text-foreground">
                        {task.title}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <StatusIcon className={cn('h-4 w-4', statusConf.className)} />
                          <span className={cn('text-sm', statusConf.className)}>
                            {statusConf.label}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityConf.className}>
                          {priorityConf.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">
                        {task.deadline ? (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            {new Date(task.deadline).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {activeTab === 'projects' && (
        <div className="border border-border rounded-lg overflow-hidden">
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No projects yet</p>
              <Button asChild>
                <Link to={`/projects/new?clientId=${client.id}`}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-medium text-foreground">
                      {project.name}
                    </TableCell>
                    <TableCell>
                      <Badge className="border-primary text-primary">
                        {project.type === 'client' ? 'Client' : 'Side Project'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-foreground">
                      {project.createdAt
                        ? new Date(project.createdAt).toLocaleDateString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {activeTab === 'details' && (
        <div className="border border-border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Type</p>
                <p className="font-medium text-foreground">
                  {client.type === 'business' ? 'Business' : 'Private'}
                </p>
              </div>
              {client.email && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="font-medium text-foreground">{client.email}</p>
                </div>
              )}
              {client.contactInfo && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Contact</p>
                  <p className="font-medium text-foreground">{client.contactInfo}</p>
                </div>
              )}
              {client.hourlyRate && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Hourly Rate</p>
                  <p className="font-medium text-foreground">€{client.hourlyRate}/hour</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {(client.street || client.city || client.zipCode || client.country) && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Address</p>
                  <p className="font-medium text-foreground whitespace-pre-line">
                    {[client.street, `${client.zipCode || ''} ${client.city || ''}`.trim(), client.country]
                      .filter(Boolean)
                      .join('\n')}
                  </p>
                </div>
              )}
              {client.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-foreground">{client.notes}</p>
                </div>
              )}
              {client.createdAt && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Client Since</p>
                  <p className="font-medium text-foreground">
                    {new Date(client.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Client"
        description={`Are you sure you want to delete "${client.name}"? This action cannot be undone.`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </AppLayout>
  );
}
